import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const services = [
  {
    num: '01',
    icon: 'ri-heart-pulse-line',
    title: 'Preventive Care & Wellness',
    desc: 'Annual physical exams, health screenings, immunizations, and lifestyle counseling to keep you at your best. We focus on catching issues early before they become serious.',
    items: ['Annual wellness exams', 'Blood pressure & cholesterol screening', 'Cancer screenings', 'Vaccinations & immunizations', 'Nutrition & weight management'],
  },
  {
    num: '02',
    icon: 'ri-stethoscope-line',
    title: 'Chronic Disease Management',
    desc: 'Personalized, ongoing care plans for patients living with long-term conditions. We partner with you to manage your health and improve your quality of life.',
    items: ['Diabetes management', 'Hypertension control', 'Asthma & COPD care', 'Heart disease monitoring', 'Thyroid disorders'],
  },
  {
    num: '03',
    icon: 'ri-parent-line',
    title: 'Pediatric Care',
    desc: 'From newborns to teenagers, our pediatric services cover every milestone. We partner with parents to ensure healthy development at every stage.',
    items: ['Newborn & infant care', 'Well-child visits', 'School & sports physicals', 'Developmental screenings', 'Adolescent health'],
  },
  {
    num: '04',
    icon: 'ri-user-heart-line',
    title: 'Senior Primary Care',
    desc: 'Specialized care designed for adults 65 and older, including Medicare Advantage coordination, fall prevention, and cognitive health assessments.',
    items: ['Medicare Advantage coordination', 'Fall risk assessment', 'Memory & cognitive screening', 'Medication management', 'Advance care planning'],
  },
  {
    num: '05',
    icon: 'ri-computer-line',
    title: 'Telehealth Visits',
    desc: 'Get the same quality care from the comfort of your home. Our secure video visits are available for most non-emergency concerns.',
    items: ['Video consultations', 'Prescription refills', 'Lab result reviews', 'Follow-up appointments', 'Mental health check-ins'],
  },
  {
    num: '06',
    icon: 'ri-mental-health-line',
    title: 'Behavioral Health',
    desc: 'Integrated mental and emotional health support as part of your overall care. We screen, counsel, and coordinate with specialists when needed.',
    items: ['Depression & anxiety screening', 'Stress management', 'Substance use counseling', 'Behavioral health referrals', 'Wellness coaching'],
  },
  {
    num: '07',
    icon: 'ri-first-aid-kit-line',
    title: 'Urgent Care & Sick Visits',
    desc: 'Same-day appointments for acute illnesses and injuries. No need for the ER — we handle most urgent concerns right in our clinic.',
    items: ['Same-day sick visits', 'Minor injury treatment', 'Infections & fevers', 'Respiratory illness', 'Urinary tract infections'],
  },
  {
    num: '08',
    icon: 'ri-test-tube-line',
    title: 'Lab & Diagnostic Services',
    desc: 'On-site laboratory testing and diagnostic imaging coordination for fast, accurate results without extra trips.',
    items: ['On-site blood draws', 'Urinalysis', 'EKG / electrocardiogram', 'Imaging referrals', 'Rapid strep & flu tests'],
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3643f89d5902114093aafe5ed37824e4.jpg"
            alt="Our Services"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/80 to-zinc-950"></div>
        </div>
        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-rose-500 via-rose-400 to-transparent"></div>
        <div className="relative z-10 px-8 md:px-16 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-rose-500"></div>
            <span className="text-rose-400 text-xs font-semibold uppercase tracking-[0.25em]">What We Offer</span>
          </div>
          <h1
            className="text-5xl md:text-7xl font-black text-white leading-tight mb-6"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Our<br /><span className="text-rose-500">Services</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl leading-relaxed">
            Comprehensive primary care services for every age and stage of life — all under one roof.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-zinc-50 py-20">
        <div className="px-8 md:px-16 lg:px-24">
          <div className="divide-y divide-zinc-200">
            {services.map((svc) => (
              <div key={svc.title} className="group grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 hover:bg-white hover:-mx-8 hover:px-8 md:hover:-mx-16 md:hover:px-16 lg:hover:-mx-24 lg:hover:px-24 transition-all duration-300">
                {/* Number + icon */}
                <div className="lg:col-span-2 flex items-start gap-4">
                  <span
                    className="text-zinc-200 font-black text-5xl group-hover:text-rose-200 transition-colors"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {svc.num}
                  </span>
                </div>
                {/* Content */}
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-zinc-100 group-hover:bg-rose-50 transition-colors shrink-0">
                      <i className={`${svc.icon} text-lg text-zinc-400 group-hover:text-rose-500 transition-colors`}></i>
                    </div>
                    <h3
                      className="text-xl font-black text-zinc-900"
                      style={{ fontFamily: "'Syne', sans-serif" }}
                    >
                      {svc.title}
                    </h3>
                  </div>
                  <p className="text-zinc-500 text-sm leading-relaxed">{svc.desc}</p>
                </div>
                {/* Items */}
                <div className="lg:col-span-5">
                  <ul className="space-y-2">
                    {svc.items.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-zinc-600 text-sm">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-rose-500 text-base"></i>
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-zinc-950 rounded-3xl p-12 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-rose-500"></div>
                <span className="text-rose-400 text-xs font-semibold uppercase tracking-[0.25em]">Get Started</span>
              </div>
              <h3
                className="text-3xl font-black text-white"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                Ready to Get Started?
              </h3>
              <p className="text-zinc-400 text-sm mt-2 max-w-md">New patients always welcome. Same-day appointments available for urgent concerns.</p>
            </div>
            <Link
              to="/book-appointment"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-rose-500 text-white font-bold rounded-full hover:bg-rose-400 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm shrink-0"
            >
              Book an Appointment
              <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full group-hover:translate-x-1 transition-transform">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
